import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CloseIcon, ErrorIcon, SuccessIcon } from './icons';
import { FOCUS_RING_FLUSH } from './ui';

/**
 * Confirmation that something happened.
 *
 * Six flows -- pin, star, archive, save, delete, and every bulk change -- used
 * to mutate and say nothing at all. On the quiet ones that is genuinely
 * ambiguous: unpinning removes the row you clicked, so the only evidence the
 * click worked is that the thing you were looking at is gone, which is also
 * what a bug looks like. And the only `aria-live` region in the whole app was
 * the bulk bar's "N selected", so a screen reader was told none of it.
 *
 * Deliberately small. Toasts are for things that worked; a failure belongs in
 * an `Alert` next to the control that failed, where it can be read at leisure
 * and carry a retry -- which is what the pages already do, and why there is no
 * `error` tone here.
 */
type ToastTone = 'success' | 'info';

/** One button on the toast, for taking back the thing it reports -- Undo. */
type ToastAction = { label: string; onAction: () => void };

type Toast = { id: number; tone: ToastTone; message: string; action?: ToastAction };

type ToastOptions = { tone?: ToastTone; action?: ToastAction };

type ToastContextValue = {
  /** Shows a toast. Returns nothing: nobody should be waiting on this. The
   *  second argument is a bare tone for the common case. */
  toast: (message: string, options?: ToastTone | ToastOptions) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Returns a no-op outside a provider rather than throwing.
 *
 * A toast is a nicety, and a component that renders in a test or a story with
 * no provider around it should still render. Nothing downstream branches on
 * whether the message was actually shown.
 */
export function useToast(): ToastContextValue {
  return useContext(ToastContext) ?? NO_TOASTS;
}

const NO_TOASTS: ToastContextValue = { toast: () => {} };

/** Long enough to read a short sentence, short enough not to sit over the page
 *  while you work. Hovering the stack pauses it -- see `Toaster`. */
const DISMISS_AFTER_MS = 4000;

/** Longer when there is something to click: reading the sentence and then
 *  deciding to undo it is two steps, not one. */
const DISMISS_ACTION_AFTER_MS = 7000;

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  /** Set while the pointer is over the stack, so a toast cannot expire out from
   *  under someone who is reading it. */
  const [paused, setPaused] = useState(false);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) window.clearTimeout(timer);
    timers.current.delete(id);
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const toast = useCallback((message: string, options?: ToastTone | ToastOptions) => {
    const { tone = 'success', action } =
      typeof options === 'string' ? { tone: options } : (options ?? {});
    const id = (nextId += 1);
    // Capped at three. They stack upwards, and a bulk action that fans out over
    // twenty rows must not bury the page it is about.
    setToasts((current) => [
      ...current.slice(-2),
      { id, tone, message, ...(action ? { action } : {}) },
    ]);
  }, []);

  // Timers live here rather than inside each toast so pausing is one flag
  // rather than a message passed down to every child.
  useEffect(() => {
    if (paused) return;

    for (const entry of toasts) {
      if (timers.current.has(entry.id)) continue;
      timers.current.set(
        entry.id,
        window.setTimeout(
          () => dismiss(entry.id),
          entry.action ? DISMISS_ACTION_AFTER_MS : DISMISS_AFTER_MS,
        ),
      );
    }
  }, [toasts, paused, dismiss]);

  // Clearing on pause rather than tracking elapsed time: the reset is generous
  // to the reader, which is the whole point of pausing.
  useEffect(() => {
    if (!paused) return;
    for (const timer of timers.current.values()) window.clearTimeout(timer);
    timers.current.clear();
  }, [paused]);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/*
        `aria-live="polite"` and not `assertive`: none of this is urgent, and
        interrupting somebody mid-sentence to tell them a pin was saved is worse
        than not telling them. The region is always in the DOM -- a live region
        that is added at the same moment as its content is not announced.
      */}
      <div
        role="status"
        aria-live="polite"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6"
      >
        {toasts.map((entry) => (
          <div
            key={entry.id}
            className="pointer-events-auto flex max-w-sm items-start gap-2.5 rounded-xl border border-stone-200 bg-white px-3.5 py-2.5 text-sm text-stone-800 shadow-lg motion-safe:animate-[fade-in-up_150ms_ease-out]"
          >
            {entry.tone === 'success' ? (
              <SuccessIcon className="mt-px h-4 w-4 shrink-0 text-emerald-600" />
            ) : (
              <ErrorIcon className="mt-px h-4 w-4 shrink-0 text-stone-400" />
            )}
            <span className="min-w-0 flex-1">{entry.message}</span>
            {entry.action ? (
              <button
                type="button"
                onClick={() => {
                  // Gone first: one press is one undo, and a toast left up
                  // after it would offer the same take-back twice.
                  dismiss(entry.id);
                  entry.action!.onAction();
                }}
                className={`-my-0.5 shrink-0 rounded-md px-1.5 py-0.5 text-sm font-medium text-honey-700 transition hover:bg-honey-50 hover:text-honey-800 ${FOCUS_RING_FLUSH}`}
              >
                {entry.action.label}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => dismiss(entry.id)}
              aria-label="Dismiss"
              className={`-m-1 shrink-0 rounded-md p-1 text-stone-400 transition hover:bg-stone-100 hover:text-stone-700 ${FOCUS_RING_FLUSH}`}
            >
              <CloseIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
